import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { useSubmitDashboardFeedback } from "@/api/queries/feedback";
import type { DashboardFeedbackType } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const feedbackSchema = z.object({
  type: z.enum(["bug", "idea", "other"]),
  description: z
    .string()
    .trim()
    .min(1, "Tell us what happened or what would help")
    .max(2_000, "Keep feedback to 2,000 characters or fewer"),
});

type FeedbackForm = z.infer<typeof feedbackSchema>;

const DEFAULT_VALUES: FeedbackForm = {
  type: "bug",
  description: "",
};

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const submitFeedback = useSubmitDashboardFeedback();
  const form = useForm<FeedbackForm>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const description = useWatch({
    control: form.control,
    name: "description",
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await submitFeedback.mutateAsync({
        type: values.type,
        description: values.description,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
      form.reset(DEFAULT_VALUES);
      onOpenChange(false);
      toast.success("Thanks — your feedback was sent");
    } catch {
      toast.error("Couldn't send feedback — try again");
    }
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!submitFeedback.isPending) onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send dashboard feedback</DialogTitle>
          <DialogDescription>
            Tell the product team what is broken or what would make your work
            easier. Page and browser details are included automatically.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            void onSubmit(event);
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="feedback-type">Feedback type</Label>
            <Controller
              control={form.control}
              name="type"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) =>
                    field.onChange(value as DashboardFeedbackType)
                  }
                  disabled={submitFeedback.isPending}
                >
                  <SelectTrigger id="feedback-type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="idea">Idea</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="feedback-description">What should we know?</Label>
            <Textarea
              id="feedback-description"
              className="min-h-32 resize-y"
              placeholder="What were you trying to do? What happened, and what did you expect?"
              maxLength={2_000}
              disabled={submitFeedback.isPending}
              aria-invalid={Boolean(form.formState.errors.description)}
              {...form.register("description")}
            />
            <div className="flex items-start justify-between gap-3">
              <div aria-live="polite">
                {form.formState.errors.description && (
                  <p className="text-destructive text-xs">
                    {form.formState.errors.description.message}
                  </p>
                )}
              </div>
              <span className="text-muted-foreground shrink-0 text-xs">
                {description.length}/2,000
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitFeedback.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitFeedback.isPending}>
              {submitFeedback.isPending ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
